import { useState, useEffect, useRef } from "react";
import useQuizStore from "@/store/useQuizStore";
import axios from "axios";

class KlaviyoLeadProvider {
  constructor() {
    this.baseUrl = "/wp-json/api/v1/email-marketing";
    this.sessionId = sessionStorage.getItem("sessionId") || null;
  }

  async initialize() {
    if (this.sessionId) {
      return this.sessionId;
    }

    try {
      const { data } = await axios.get(`${this.baseUrl}/session`);
      this.sessionId = data.sessionId;
      sessionStorage.setItem("sessionId", this.sessionId);
      return this.sessionId;
    } catch (error) {
      throw new Error("Unable to initialize session. Please try again later.");
    }
  }

  async createLead(leadData) {
    if (!this.sessionId) {
      throw new Error(
        "Session not available. Please refresh the page and try again.",
      );
    }

    try {
      const { data } = await axios.post(
        `${this.baseUrl}/create-email-lead`,
        leadData,
        {
          headers: {
            "X-Session-Id": this.sessionId,
          },
        },
      );
      return data;
    } catch (error) {
      throw new Error("Failed to process your request.");
    }
  }
}

export const useKlaviyoLead = () => {
  const providerRef = useRef(null);
  const { quizData, preferredUnit } = useQuizStore();
  const [isProviderReady, setIsProviderReady] = useState(false);

  useEffect(() => {
    const initProvider = async () => {
      try {
        const provider = new KlaviyoLeadProvider();
        await provider.initialize();
        providerRef.current = provider;
        setIsProviderReady(true);
      } catch (error) {
        console.error("Failed to initialize Klaviyo lead provider:", error);
      }
    };

    initProvider();
  }, []);

  const createLead = async () => {
    if (!isProviderReady || !providerRef.current) return;

    const email = quizData.email;
    if (!email) return;

    const currentWeight =
      preferredUnit === "metric"
        ? quizData.currentWeightKg
        : quizData.currentWeightLb;
    const targetWeight =
      preferredUnit === "metric"
        ? quizData.goalWeightKg
        : quizData.goalWeightLb;
    const gender = Array.isArray(quizData.gender)
      ? quizData.gender[0] || ""
      : quizData.gender || "";

    if (!currentWeight || !targetWeight || !gender) return;

    const leadData = {
      email,
      current_weight: String(currentWeight),
      target_weight: String(targetWeight),
      gender,
    };

    try {
      await providerRef.current.createLead(leadData);
      return true;
    } catch (error) {
      console.error("Failed to create Klaviyo lead", error);
      return false;
    }
  };

  return { createLead, isProviderReady };
};

export default KlaviyoLeadProvider;
